import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import ActivityStatus from "@mapmycustomers/shared/enum/activity/ActivityStatus";
import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import {
  Activity,
  EntityType,
  EntityTypeSupportingSourceTracking,
  Group,
  MapEntity,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import ActivityTypeWithCount from "@mapmycustomers/shared/types/entity/activities/ActivityTypeWithCount";
import { EntityTypeSupportingCadence } from "@mapmycustomers/shared/types/entity/activities/OutOfCadenceEntity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { StageSummary } from "@mapmycustomers/shared/types/entity/deals/StageSummary";
import { MapEntry } from "@mapmycustomers/shared/types/map";
import PlatformBounds from "@mapmycustomers/shared/types/map/PlatformBounds";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import { SupportedSources } from "@app/enum/SourceType";
import { ActivityCount } from "@app/scene/home/component/Cards/util";
import { GroupByType } from "@app/scene/reports/components/Overview/components/Engagement/enum/GroupByType";
import SourceSummary from "@app/types/source/SourceSummary";
import Statistics from "@app/types/Statistics";

import { DealsGroupByType } from "../enum/DealsGroupByType";
import { DealsGroupBy } from "../types/DealGroupBy";
import { DealsRottingFilter } from "../types/DealsRottingFilter";
import OpenDealsCardData from "../types/OpenDealsCardData";
import { SupportingGroupsRecordStats } from "../types/RecordsOutOfCadence";
import RecordsOutOfCadenceFilter from "../types/RecordsOutOfCadenceFilter";
import RecordsSourceFilter from "../types/RecordsSourceFilter";
import { RecordsStats } from "../types/RecordsStats";

export const initialize = createAsyncAction(
  "home/initialize/request",
  "home/initialize/success",
  "home/initialize/failure"
)<
  void,
  {
    dealsCount: number;
    dealsRottingFilter: DealsRottingFilter;
    hasActivities: boolean;
    hasCompanies: boolean;
    recordsOutOfCadenceFilter: RecordsOutOfCadenceFilter;
    recordsSourceFilter: RecordsSourceFilter;
  },
  unknown
>();

export const fetchTodayActivitiesData = createAsyncAction(
  "home/fetchTodayActivitiesData/request",
  "home/fetchTodayActivitiesData/success",
  "home/fetchTodayActivitiesData/failure"
)<
  {
    activityTypes: Array<ActivityType>;
    assigneeIds: Array<User["id"]>;
    date: Date;
    status: ActivityStatusOption;
  },
  Activity[],
  unknown
>();

export const fetchActivityByRepData = createAsyncAction(
  "home/fetchActivityByRepData/request",
  "home/fetchActivityByRepData/success",
  "home/fetchActivityByRepData/failure"
)<
  {
    activityTypeIds: Array<ActivityType["id"]>;
    assigneeIds: Array<User["id"]>;
    dateRange: DateRange;
  },
  Record<User["id"], ActivityCount[]>,
  unknown
>();

export const fetchActivityByTypesData = createAsyncAction(
  "home/fetchActivityByTypesData/request",
  "home/fetchActivityByTypesData/success",
  "home/fetchActivityByTypesData/failure"
)<
  {
    assigneeIds: Array<User["id"]>;
    crmActivityTypeIds: Array<ActivityType["id"]>;
    dateRange: DateRange;
  },
  ActivityTypeWithCount[],
  unknown
>();

export const fetchSelectedActivityByTypesData = createAsyncAction(
  "home/fetchSelectedActivityByTypesData/request",
  "home/fetchSelectedActivityByTypesData/success",
  "home/fetchSelectedActivityByTypesData/failure"
)<
  {
    assigneeIds: Array<User["id"]>;
    crmActivityTypeIds: ActivityType["id"];
    dateRange: DateRange;
    page?: number;
  },
  { activities: Activity[]; totalRecords: number },
  unknown
>();

export const fetchTodayActivitiesDataForReps = createAsyncAction(
  "home/fetchTodayActivitiesDataForReps/request",
  "home/fetchTodayActivitiesDataForReps/success",
  "home/fetchTodayActivitiesDataForReps/failure"
)<
  {
    dateRange: string;
    status: ActivityStatus;
  },
  Activity[],
  unknown
>();

export const fetchFunnelSummary = createAsyncAction(
  "home/fetchFunnelSummary/request",
  "home/fetchFunnelSummary/success",
  "home/fetchFunnelSummary/failure"
)<{ funnelId: Funnel["id"]; selectedUserIds: User["id"][] }, StageSummary[], unknown>();

export const fetchRecordsBySourceSummary = createAsyncAction(
  "home/fetchRecordsBySourceSummary/request",
  "home/fetchRecordsBySourceSummary/success",
  "home/fetchRecordsBySourceSummary/failure"
)<
  {
    dateRange: string;
    entityTypes: EntityTypeSupportingSourceTracking[];
    userId: Array<User["id"]>;
  },
  SourceSummary[],
  unknown
>();

export const fetchRecordsBySourceForSelectedType = createAsyncAction(
  "home/fetchRecordsBySourceForSelectedType/request",
  "home/fetchRecordsBySourceForSelectedType/success",
  "home/fetchRecordsBySourceForSelectedType/failure"
)<
  {
    dateRange: string;
    entityTypes: Exclude<EntityTypeSupportingSourceTracking, EntityType.ACTIVITY>[];
    page?: number;
    sourceType: SupportedSources;
    userId: Array<User["id"]>;
  },
  { recordsBySourceList: MapEntity[]; totalRecords: number },
  unknown
>();

export const fetchRecentCompanies = createAsyncAction(
  "home/fetchRecentCompanies/request",
  "home/fetchRecentCompanies/success",
  "home/fetchRecentCompanies/failure"
)<
  { bounds?: PlatformBounds; limit?: number; usersIds?: User["id"][]; zoom?: number },
  MapEntry[],
  unknown
>();

export const fetchCompaniesCount = createAsyncAction(
  "home/fetchCompaniesCount/request",
  "home/fetchCompaniesCount/success",
  "home/fetchCompaniesCount/failure"
)<void, boolean, unknown>();

export const fetchOpenDealsData = createAsyncAction(
  "home/fetchOpenDealsData/request",
  "home/fetchOpenDealsData/success",
  "home/fetchOpenDealsData/failure"
)<{ funnelId: Funnel["id"]; usersIds: User["id"][] }, OpenDealsCardData, void>();

export const fetchActivityOverview = createAsyncAction(
  "home/fetchActivityOverview/request",
  "home/fetchActivityOverview/>success",
  "home/fetchActivityOverview/>failure"
)<{ dateRange: string; usersIds: User["id"][] }, Statistics["values"], unknown>();

export const setRecordSourceFilter = createAction(
  "home/setRecordSourceFilter"
)<RecordsSourceFilter>();

export const setRecordsOutOfCadenceFilter = createAction(
  "home/setRecordsOutOfCadenceFilter"
)<RecordsOutOfCadenceFilter>();

export const fetchRecordsOutOfCadence = createAsyncAction(
  "home/fetchRecordsOutOfCadence/request",
  "home/fetchRecordsOutOfCadence/success",
  "home/fetchRecordsOutOfCadence/failure"
)<
  {
    groupIds?: Group["id"][];
    teamIds?: Team["id"][];
    userIds?: User["id"][];
  },
  SupportingGroupsRecordStats,
  unknown
>();

export const setDealsRottingFilter = createAction(
  "home/setDealsRottingFilter"
)<DealsRottingFilter>();

export const fetchDealsRotting = createAsyncAction(
  "home/fetchDealsRotting/request",
  "home/fetchDealsRotting/success",
  "home/fetchDealsRotting/failure"
)<
  {
    dealsGroupByType: DealsGroupByType;
  },
  {
    dealsRottingStats: Record<DealsGroupBy["id"], RecordsStats>;
    stagesStats: Record<Funnel["id"], Record<Stage["name"], RecordsStats>>;
  },
  unknown
>();

export const fetchEngagementRecordsList = createAsyncAction(
  "home/fetchEngagementRecordsList/request",
  "home/fetchEngagementRecordsList/success",
  "home/fetchEngagementRecordsList/failure"
)<
  {
    id: Group["id"] | Team["id"] | User["id"];
    page?: number;
    recordTypes: EntityTypeSupportingCadence[];
    type: GroupByType;
  },
  { records: MapEntity[]; totalRecords: number },
  unknown
>();

export type Actions = ActionType<
  | typeof fetchActivityByRepData
  | typeof fetchActivityByTypesData
  | typeof fetchActivityOverview
  | typeof fetchCompaniesCount
  | typeof fetchDealsRotting
  | typeof fetchEngagementRecordsList
  | typeof fetchFunnelSummary
  | typeof fetchOpenDealsData
  | typeof fetchRecentCompanies
  | typeof fetchRecordsBySourceForSelectedType
  | typeof fetchRecordsBySourceSummary
  | typeof fetchRecordsOutOfCadence
  | typeof fetchSelectedActivityByTypesData
  | typeof fetchTodayActivitiesData
  | typeof fetchTodayActivitiesDataForReps
  | typeof initialize
  | typeof setDealsRottingFilter
  | typeof setRecordSourceFilter
  | typeof setRecordsOutOfCadenceFilter
>;
